import React, { useMemo } from 'react';
import SEOHead from '../components/SEOHead';
import { getOrganizationSchema, getWebSiteSchema } from '../utils/seoSchemas';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Sparkles, MapPin, Plane, ArrowRight, Map, Hotel, Bus, Ticket } from 'lucide-react';
import { matchesAPI, newsAPI, teamsAPI, stadiumsAPI, citiesAPI } from '../services/api';
import AdBanner from '../components/AdBanner';
import { GUIDE_ARTICLES } from '../content/guides/articles';
import { ITINERARY_HUB_HOSTS, itineraryHubPath } from '../content/guides/itineraryHubMeta';

const asArray = (data) => (Array.isArray(data) ? data : []);

const HERO_STADIUM_IMG =
  'https://lh3.googleusercontent.com/aida-public/AB6AXuBT1UnrFUwOBnoHLHyTPdfkwCaXG1I9BVZHGdNL4WoHvwjozTt1_utDkh0Q-PuOt3_CxDG-GBvjKhT9j6KjGxfjBL2EJBXDDz-ZSpA3lIccHyIjyeqqP4wHMBFGFH7E_lWik7uhytpyefG_9Z0x3ui_RSr5sf1kC-gcsfa07tkQdCcCg66rcDhGVtUnF58hsRBgF6jXiH-nu8sdc2Ccygkh40NWTb1XO8ldF-RcWxIJ5RTvD2JEHT51STLGT-FsdyHM6Y0Tvuch8xng';

const HOTEL_IMG =
  'https://lh3.googleusercontent.com/aida-public/AB6AXuC7O3TTho6XQdcvcN7guQYPs7JjVPqYhCRb784B-qKXwICRkjdJ2JLv2_ZY5-riT5bpnap7j99Iq6dB7tLS38roQNtCctWnbsmpwhxIEDmiNH5NeF7jP9GC9JRX6TFwjn8RAK158eqwXyfVLU4jIrxKNAe-3XxL1nHlQaMvOgoYXztzu4ZbRWLDs90yiJEsP5Wjq6g9qy9UsukUGhAHna53OUCvy_3WseQAVDR6MfwzGjP_AVm5vxxxmZOpJloS0isMOlkBtVH10dfp';

const FLIGHT_IMG =
  'https://lh3.googleusercontent.com/aida-public/AB6AXuCvmpbRKEqm0HP05M-vJ9HNqn_oHAHuJEGX8w7Rs8e8nbkCQ2RU0pLuRUBsC4Ar1O4IA9bYAs0ti0-8sl8KLF5_sblxQlNtPkpOKfC9-oyNWtn48tzC7LDrAZZhFxcuiffRdpDQpk0ezSfh6UETEPuSdhK68onNBr9X5N0pwgSvguKawK7yAuiy_G--v8LhFQ2lf_-0PjKcPX86W31gkZLz-WsqJRNVti2DLc87aRcM-1XS11Icq1dtgxNxIuhNqb1_Xw1bEfOxm-DN';

function teamInitials(name) {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const Home = () => {
  const { data: matches = [] } = useQuery({ queryKey: ['matches'], queryFn: () => matchesAPI.getAll().then((r) => asArray(r.data)) });
  const { data: news = [] } = useQuery({ queryKey: ['news'], queryFn: () => newsAPI.getAll().then((r) => asArray(r.data)) });
  const { data: teams = [] } = useQuery({ queryKey: ['teams'], queryFn: () => teamsAPI.getAll().then((r) => asArray(r.data)) });
  const { data: stadiums = [] } = useQuery({ queryKey: ['stadiums'], queryFn: () => stadiumsAPI.getAll().then((r) => asArray(r.data)) });
  const { data: cities = [] } = useQuery({ queryKey: ['cities'], queryFn: () => citiesAPI.getAll().then((r) => asArray(r.data)) });

  const featuredMatches = matches.slice(0, 2);
  const newsFeatures = news.slice(0, 2);

  const usHostCities = useMemo(() => {
    const list = cities.filter((c) => {
      const co = (c.country || '').toLowerCase();
      return co.includes('united states') || co === 'usa' || co === 'us';
    });
    return list.length ? list.slice(0, 12) : cities.slice(0, 12);
  }, [cities]);

  const featuredGuides = useMemo(() => GUIDE_ARTICLES.slice(0, 4), []);

  return (
    <>
      <SEOHead
        title="Plan Your FIFA World Cup 2026 Trip in the USA | WorldCup Fan Planner"
        description="FIFA World Cup 2026 travel planning for the United States: U.S. host cities, stadium guides, match schedule, AI itineraries, budgets, and fan tips."
        schema={[getOrganizationSchema(), getWebSiteSchema()]}
      />
      <main className="overflow-x-hidden">
        <section className="relative min-h-[min(870px,100vh)] flex items-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={HERO_STADIUM_IMG}
              alt=""
              className="w-full h-full object-cover opacity-80 mix-blend-multiply dark:opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-transparent dark:from-primary-dim/95" aria-hidden />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 w-full py-16">
            <div className="max-w-3xl">
              <span className="inline-block px-4 py-1 bg-tertiary text-on-tertiary font-bold text-xs tracking-widest mb-6 rounded-sm uppercase">
                USA travel hub · 2026
              </span>
              <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-headline font-bold text-white leading-tight tracking-tighter mb-8">
                Plan your FIFA World Cup 2026 trip <br />
                <span className="text-primary-container">in the United States</span>
              </h1>
              <p className="text-lg sm:text-xl text-white/90 mb-10 font-body max-w-xl leading-relaxed">
                Host cities, stadium guides, fixtures, and an AI itinerary builder—built for fans flying to U.S. venues (with Canada and Mexico still in the tournament
                map when you branch out).
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/cities"
                  className="kinetic-gradient text-on-primary px-8 sm:px-10 py-4 sm:py-5 rounded-xl font-bold text-base sm:text-lg shadow-xl hover:brightness-110 active:scale-95 transition-all inline-flex items-center justify-center"
                >
                  Browse U.S. host cities
                </Link>
                <Link
                  to="/matches"
                  className="bg-white/10 backdrop-blur-md border border-white/20 text-white px-8 sm:px-10 py-4 sm:py-5 rounded-xl font-bold text-base sm:text-lg hover:bg-white/20 transition-all inline-flex items-center justify-center"
                >
                  Match schedule
                </Link>
                <Link
                  to="/guides/budget-world-cup-2026"
                  className="bg-white/10 backdrop-blur-md border border-white/20 text-white px-8 sm:px-10 py-4 sm:py-5 rounded-xl font-bold text-base sm:text-lg hover:bg-white/20 transition-all inline-flex items-center justify-center"
                >
                  Budget guide
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-surface-container-low dark:bg-slate-900/50 py-16 sm:py-20 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12 text-center md:text-left">
              <div className="space-y-4">
                <div className="text-6xl sm:text-7xl font-black text-primary tracking-tighter">{usHostCities.length || cities.length || 16}</div>
                <div className="h-1 w-12 bg-tertiary mx-auto md:mx-0" />
                <h2 className="text-2xl font-headline font-bold text-on-surface dark:text-white">U.S. host cities</h2>
                <p className="text-on-surface-variant dark:text-slate-400">
                  Start with American venues—then add Canada or Mexico legs when your tickets and flights line up.
                </p>
              </div>
              <div className="space-y-4">
                <div className="text-6xl sm:text-7xl font-black text-secondary tracking-tighter">{stadiums.length || 16}</div>
                <div className="h-1 w-12 bg-secondary mx-auto md:mx-0" />
                <h2 className="text-2xl font-headline font-bold text-on-surface dark:text-white">Stadiums</h2>
                <p className="text-on-surface-variant dark:text-slate-400">
                  Modern arenas and historic venues ready to host the beautiful game.
                </p>
              </div>
              <div className="space-y-4">
                <div className="text-6xl sm:text-7xl font-black text-tertiary tracking-tighter">{matches.length || 104}</div>
                <div className="h-1 w-12 bg-primary mx-auto md:mx-0" />
                <h2 className="text-2xl font-headline font-bold text-on-surface dark:text-white">Matches</h2>
                <p className="text-on-surface-variant dark:text-slate-400">
                  Follow fixtures, venues, and travel angles for every match window.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 sm:py-18 bg-surface dark:bg-slate-950/20 border-y border-outline-variant/10 dark:border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-8">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
              <div>
                <h2 className="text-2xl sm:text-3xl font-headline font-bold text-on-surface dark:text-white mb-2">U.S. city guides &amp; trip ideas</h2>
                <p className="text-on-surface-variant dark:text-slate-400 max-w-2xl">
                  Jump into long-form guides for Seattle, Los Angeles, and New York metro—plus itineraries and budgets. Every guide links to the AI itinerary planner
                  with suggested settings.
                </p>
              </div>
              <Link to="/guides" className="text-primary font-bold flex items-center gap-2 shrink-0">
                All guides
                <ArrowRight className="h-5 w-5" aria-hidden />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
              {featuredGuides.map((g) => (
                <Link
                  key={g.slug}
                  to={`/guides/${g.slug}`}
                  className="p-5 rounded-xl bg-surface-container-lowest dark:bg-slate-800/80 border border-outline-variant/10 dark:border-slate-700 hover:border-primary/40 transition-colors"
                >
                  <span className="font-bold text-on-surface dark:text-white line-clamp-2">{g.title}</span>
                  <p className="text-sm text-on-surface-variant dark:text-slate-400 mt-2 line-clamp-2">{g.description}</p>
                </Link>
              ))}
            </div>
            <h3 className="text-lg font-headline font-bold text-on-surface dark:text-white mb-4">Explore U.S. host cities</h3>
            <div className="flex flex-wrap gap-2">
              {usHostCities.map((c) => (
                <Link
                  key={c.id || c.slug}
                  to={`/cities/${c.slug || c.id}`}
                  className="px-4 py-2 rounded-full bg-surface-container-low dark:bg-slate-800 text-sm font-medium text-on-surface dark:text-slate-200 border border-outline-variant/15 dark:border-slate-600 hover:border-primary/50"
                >
                  {c.name}
                </Link>
              ))}
              {usHostCities.length === 0 && (
                <p className="text-on-surface-variant text-sm">Load cities from the API to see U.S. host links.</p>
              )}
            </div>

            <h3 className="text-lg font-headline font-bold text-on-surface dark:text-white mt-10 mb-3">AI itinerary hubs (3 / 5 / 7 days)</h3>
            <p className="text-sm text-on-surface-variant dark:text-slate-400 max-w-2xl mb-4">
              One page per host region with tabs for trip length—ideal for sharing or saving. Canada and Mexico hosts are included for full tournament routing.
            </p>
            <div className="flex flex-wrap gap-2">
              {ITINERARY_HUB_HOSTS.slice(0, 6).map((h) => (
                <Link
                  key={h.guideSlug}
                  to={itineraryHubPath(h.guideSlug)}
                  className="px-4 py-2 rounded-full bg-primary-container/25 dark:bg-primary-dim/30 text-sm font-medium text-on-surface dark:text-slate-100 border border-primary/20 dark:border-primary/30 hover:border-primary/50"
                >
                  {h.metroLabel}
                </Link>
              ))}
              <Link
                to="/guides"
                className="px-4 py-2 rounded-full text-sm font-bold text-primary dark:text-primary-container border border-primary/40 hover:bg-primary/10"
              >
                All guides
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-24 bg-surface dark:bg-slate-950/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-8">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 mb-12 sm:mb-16">
              <div>
                <h2 className="text-3xl sm:text-4xl font-headline font-bold tracking-tight text-on-surface dark:text-white mb-2">Top Upcoming Matches</h2>
                <p className="text-on-surface-variant dark:text-slate-400">The biggest matchups on the road to the final.</p>
              </div>
              <Link to="/matches" className="text-primary font-bold flex items-center gap-2 group shrink-0">
                View All Matches
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" aria-hidden />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredMatches.map((m, idx) => {
                const mid = m.match_number ?? m.id;
                const borderAccent = idx === 0 ? 'bg-tertiary' : 'bg-secondary';
                return (
                  <article
                    key={m.id}
                    className="bg-surface-container-lowest dark:bg-slate-800/80 rounded-xl p-6 sm:p-8 flex flex-col justify-between h-full relative overflow-hidden border border-outline-variant/10 dark:border-slate-700"
                  >
                    <div className={`absolute top-0 left-0 w-full h-1 ${borderAccent}`} aria-hidden />
                    <div>
                      <div className="flex justify-between items-center mb-6">
                        <span className="bg-surface-container text-on-surface dark:bg-slate-700 dark:text-slate-200 px-3 py-1 rounded text-xs font-bold uppercase tracking-wider">
                          {m.group_stage || 'Fixture'}
                        </span>
                        <span className="text-on-surface-variant text-xs font-medium">
                          {m.date ? new Date(m.date).toLocaleDateString() : 'TBD'}
                        </span>
                      </div>
                      <div className="space-y-4 mb-8">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-surface-container-low dark:bg-slate-700 rounded-full flex items-center justify-center font-bold text-lg text-on-surface dark:text-white">
                            {teamInitials(m.team1_name)}
                          </div>
                          <span className="text-xl sm:text-2xl font-bold text-on-surface dark:text-white">{m.team1_name || 'TBD'}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-surface-container-low dark:bg-slate-700 rounded-full flex items-center justify-center font-bold text-lg text-on-surface dark:text-white">
                            {teamInitials(m.team2_name)}
                          </div>
                          <span className="text-xl sm:text-2xl font-bold text-on-surface dark:text-white">{m.team2_name || 'TBD'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="pt-6 border-t border-surface-container-high dark:border-slate-600">
                      <p className="text-sm font-medium text-on-surface-variant mb-4 flex items-center gap-2">
                        <MapPin className="h-4 w-4 shrink-0" aria-hidden />
                        {m.stadium_name || 'Venue TBD'}
                      </p>
                      <div className="flex flex-col gap-2">
                        <Link
                          to={mid != null ? `/matches/${mid}` : '/matches'}
                          className="w-full py-3 text-center bg-secondary-container text-on-secondary-container rounded-lg font-bold hover:bg-secondary hover:text-on-secondary transition-colors"
                        >
                          Match details
                        </Link>
                        <Link
                          to="/tickets"
                          className="w-full py-3 text-center bg-secondary-container/60 dark:bg-secondary/20 text-secondary dark:text-blue-300 rounded-lg font-bold hover:bg-secondary-container transition-colors"
                        >
                          Find Tickets
                        </Link>
                      </div>
                    </div>
                  </article>
                );
              })}
              {featuredMatches.length === 0 && (
                <p className="col-span-full text-center text-on-surface-variant py-8">No matches loaded yet. Start the API and seed data to see fixtures.</p>
              )}
              <aside className="bg-primary dark:bg-primary-dim rounded-xl p-8 flex flex-col justify-center relative overflow-hidden min-h-[280px] border border-white/10">
                <div
                  className="absolute inset-0 opacity-10 pointer-events-none"
                  style={{
                    backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.06) 10px, rgba(255,255,255,0.06) 20px)',
                  }}
                  aria-hidden
                />
                <div className="relative z-10">
                  <h3 className="text-2xl font-headline font-bold text-white mb-3">Plan Your Custom Experience</h3>
                  <p className="text-on-primary/90 mb-6 leading-relaxed">
                    Let AI shape stadium days, food stops, and fan zones around your city and dates.
                  </p>
                  <Link
                    to="/itinerary-planner"
                    className="inline-flex items-center gap-2 bg-white text-primary px-6 py-3 rounded-xl font-bold hover:bg-primary-container transition-colors"
                  >
                    <Sparkles className="h-5 w-5" aria-hidden />
                    Try AI Planner
                  </Link>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <AdBanner className="my-8" slot="homepage-banner" />
        </div>

        <section className="bg-surface-container-low dark:bg-slate-900/50 py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-8">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-3xl sm:text-4xl font-headline font-bold text-on-surface dark:text-white mb-4">Travel partners</h2>
              <p className="text-on-surface-variant dark:text-slate-400 max-w-2xl mx-auto">
                Book flights and stays through trusted paths—we link to major partners to help you compare options.
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-surface-container-lowest dark:bg-slate-800/60 p-6 sm:p-8 rounded-2xl flex flex-col md:flex-row gap-8 items-center border border-outline-variant/10 dark:border-slate-700">
                <div className="w-full md:w-1/3 aspect-video md:aspect-square bg-surface-container-high rounded-xl overflow-hidden shrink-0">
                  <img src={HOTEL_IMG} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-xl font-headline font-bold text-on-surface dark:text-white mb-2">Stadium-Side Stays</h3>
                  <p className="text-on-surface-variant dark:text-slate-400 text-sm mb-6 leading-relaxed">
                    Compare hotels and fan-friendly bases near host cities and venues.
                  </p>
                  <Link
                    to="/hotels"
                    className="inline-flex items-center gap-2 bg-secondary text-on-secondary px-6 py-3 rounded-xl font-bold hover:brightness-110 transition-all"
                  >
                    Search Hotels
                    <Plane className="h-4 w-4" aria-hidden />
                  </Link>
                </div>
              </div>
              <div className="bg-surface-container-lowest dark:bg-slate-800/60 p-6 sm:p-8 rounded-2xl flex flex-col md:flex-row gap-8 items-center border border-outline-variant/10 dark:border-slate-700">
                <div className="w-full md:w-1/3 aspect-video md:aspect-square bg-surface-container-high rounded-xl overflow-hidden shrink-0">
                  <img src={FLIGHT_IMG} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                    <Plane className="h-5 w-5 text-secondary" aria-hidden />
                    <span className="font-bold text-lg text-on-surface dark:text-white">Flights</span>
                  </div>
                  <h3 className="text-xl font-headline font-bold text-on-surface dark:text-white mb-2">Transcontinental Flights</h3>
                  <p className="text-on-surface-variant dark:text-slate-400 text-sm mb-6 leading-relaxed">
                    Use our travel planner to structure multi-city hops across the USA, Canada, and Mexico.
                  </p>
                  <Link
                    to="/travel-planner"
                    className="inline-flex items-center gap-2 bg-secondary text-on-secondary px-6 py-3 rounded-xl font-bold hover:brightness-110 transition-all"
                  >
                    Compare Flights
                    <Plane className="h-4 w-4" aria-hidden />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-24 bg-surface dark:bg-slate-950/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
              <div className="lg:col-span-8">
                <h2 className="text-3xl sm:text-4xl font-headline font-bold text-on-surface dark:text-white mb-10">Latest Tournament News</h2>
                <div className="space-y-10">
                  {newsFeatures.map((n, i) => (
                    <article key={n.id || n.url || i} className="group">
                      <a href={n.url} target="_blank" rel="noopener noreferrer" className="flex flex-col md:flex-row gap-6 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary">
                        <div className="md:w-64 h-40 bg-surface-container-high dark:bg-slate-800 rounded-xl overflow-hidden shrink-0">
                          {n.image_url ? (
                            <img src={n.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-primary/30 to-secondary/30" aria-hidden />
                          )}
                        </div>
                        <div className="py-1">
                          <span className="text-primary font-bold text-xs uppercase tracking-widest mb-2 block">{n.source || 'Update'}</span>
                          <h3 className="text-xl sm:text-2xl font-headline font-bold text-on-surface dark:text-white mb-3 group-hover:text-primary transition-colors">
                            {n.title}
                          </h3>
                          <p className="text-on-surface-variant dark:text-slate-400 line-clamp-2">{n.description}</p>
                        </div>
                      </a>
                    </article>
                  ))}
                  {newsFeatures.length === 0 && (
                    <p className="text-on-surface-variant">Pulling headlines from our feed—check back soon or visit the News page.</p>
                  )}
                </div>
              </div>
              <div className="lg:col-span-4 bg-surface-container-low dark:bg-slate-800/50 p-6 sm:p-8 rounded-2xl h-fit border border-outline-variant/10 dark:border-slate-700">
                <h3 className="text-xl font-headline font-bold text-on-surface dark:text-white mb-6">Quick Resources</h3>
                <ul className="space-y-3">
                  <li>
                    <Link
                      to="/stadiums"
                      className="flex items-center gap-4 p-4 bg-surface-container-lowest dark:bg-slate-900/50 rounded-xl hover:shadow-md transition-shadow group border border-transparent hover:border-outline-variant/10"
                    >
                      <Map className="h-6 w-6 text-primary shrink-0" aria-hidden />
                      <span className="font-bold text-on-surface dark:text-white">Venues &amp; stadiums</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/hotels"
                      className="flex items-center gap-4 p-4 bg-surface-container-lowest dark:bg-slate-900/50 rounded-xl hover:shadow-md transition-shadow group"
                    >
                      <Hotel className="h-6 w-6 text-secondary shrink-0" aria-hidden />
                      <span className="font-bold text-on-surface dark:text-white">Accommodation Guide</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/tickets"
                      className="flex items-center gap-4 p-4 bg-surface-container-lowest dark:bg-slate-900/50 rounded-xl hover:shadow-md transition-shadow group"
                    >
                      <Ticket className="h-6 w-6 text-tertiary shrink-0" aria-hidden />
                      <span className="font-bold text-on-surface dark:text-white">Ticketing FAQ</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/cities"
                      className="flex items-center gap-4 p-4 bg-surface-container-lowest dark:bg-slate-900/50 rounded-xl hover:shadow-md transition-shadow group"
                    >
                      <Bus className="h-6 w-6 text-primary shrink-0" aria-hidden />
                      <span className="font-bold text-on-surface dark:text-white">Host cities &amp; transport</span>
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 bg-surface-container-low dark:bg-slate-900/40 border-t border-outline-variant/10 dark:border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link
              to="/teams"
              className="p-4 rounded-xl bg-surface-container-lowest dark:bg-slate-800/80 border border-outline-variant/10 dark:border-slate-700 hover:shadow-fanhub transition-shadow text-center"
            >
              <div className="text-2xl font-black text-primary">{teams.length}</div>
              <div className="text-sm text-on-surface-variant dark:text-slate-400 font-medium">Teams</div>
            </Link>
            <Link
              to="/stadiums"
              className="p-4 rounded-xl bg-surface-container-lowest dark:bg-slate-800/80 border border-outline-variant/10 dark:border-slate-700 hover:shadow-fanhub transition-shadow text-center"
            >
              <div className="text-2xl font-black text-secondary">{stadiums.length}</div>
              <div className="text-sm text-on-surface-variant dark:text-slate-400 font-medium">Stadiums</div>
            </Link>
            <Link
              to="/cities"
              className="p-4 rounded-xl bg-surface-container-lowest dark:bg-slate-800/80 border border-outline-variant/10 dark:border-slate-700 hover:shadow-fanhub transition-shadow text-center"
            >
              <div className="text-2xl font-black text-tertiary">{cities.length}</div>
              <div className="text-sm text-on-surface-variant dark:text-slate-400 font-medium">Host Cities</div>
            </Link>
            <Link
              to="/matches"
              className="p-4 rounded-xl bg-surface-container-lowest dark:bg-slate-800/80 border border-outline-variant/10 dark:border-slate-700 hover:shadow-fanhub transition-shadow text-center"
            >
              <div className="text-2xl font-black text-primary">{matches.length}</div>
              <div className="text-sm text-on-surface-variant dark:text-slate-400 font-medium">Matches</div>
            </Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default Home;
